/**
 * Quick Question API client (T35.4 / ADR-012).
 *
 * A thin wrapper for `/api/quick-questions`, self-contained inside the
 * quick-questions feature module — nothing unrelated reaches these endpoints.
 *
 * Auth: the app shell injects the shared HttpClient, whose TokenProvider works
 * on web and native and refreshes an expired access token once.
 */

import { ApiError, type HttpClient } from '@shopista/shared/api';

let client: HttpClient | null = null;

export function setQuickQuestionHttpClient(httpClient: HttpClient): void {
  client = httpClient;
}

function http(): HttpClient {
  if (!client) throw new Error('Quick Questions HTTP client has not been configured');
  return client;
}

function legacyError(error: unknown, fallback: string): never {
  if (error instanceof ApiError) {
    const err = new Error(fallback) as Error & { status?: number };
    err.status = error.status;
    throw err;
  }
  throw error;
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/** The three answer shapes a quick question can take (the server's CHECK vocab). */
export type QuickQuestionAnswerType = 'choice' | 'reaction' | 'sentence';

/**
 * The single eligible quick question the server offers this caller (or none).
 * `choices` is the tap list for a `choice` question. The server applies targeting
 * + the frequency cap; the client never decides eligibility.
 */
export interface EligibleQuickQuestion {
  id: string;
  text: string;
  answerType: QuickQuestionAnswerType;
  choices: string[] | null;
}

/** What the prompt submits when the user answers. */
export interface QuickQuestionAnswerSubmission {
  /** The structured tap-choice / reaction (a scalar or a small object); optional. */
  answerValue?: unknown;
  /** The optional free-text sentence → stored as `answertext` on the response row. */
  freeText?: string | null;
}

// ---------------------------------------------------------------------------
// API functions
// ---------------------------------------------------------------------------

/**
 * GET the one eligible quick question, or null when there is none (the common
 * case — the cap suppresses most of the time). A failed read resolves to null so
 * the prompt simply doesn't show; it never blocks the Feedback tab.
 */
export async function getEligibleQuickQuestion(): Promise<EligibleQuickQuestion | null> {
  let body: { question: { id: string; text: string; answer_type: QuickQuestionAnswerType; choices: string[] | null } | null };
  try { body = await http().getRaw('/api/quick-questions') as typeof body; }
  catch (error) { return legacyError(error, `GET /api/quick-questions failed: ${(error as ApiError).status}`); }
  // The server returns the consumer shape (snake_case answer_type); map to camel.
  if (!body.question) return null;
  return {
    id: body.question.id,
    text: body.question.text,
    answerType: body.question.answer_type,
    choices: body.question.choices,
  };
}

/** POST an answer to a quick question (optionally with structured value + free text). */
export async function answerQuickQuestion(
  id: string,
  submission: QuickQuestionAnswerSubmission,
): Promise<void> {
  await postQuickQuestionResponse(id, {
    action: 'answer',
    answerValue: submission.answerValue,
    freeText: submission.freeText ?? null,
  });
}

/** POST a dismissal of a quick question (no penalty — just records the dismissal). */
export async function dismissQuickQuestion(id: string): Promise<void> {
  await postQuickQuestionResponse(id, { action: 'dismiss' });
}

/** Shared POST to the answer/dismiss endpoint. Rejects on a non-2xx. */
async function postQuickQuestionResponse(
  id: string,
  body: { action: 'answer' | 'dismiss'; answerValue?: unknown; freeText?: string | null },
): Promise<void> {
  try { await http().postVoid(`/api/quick-questions/${id}`, body); }
  catch (error) { legacyError(error, `POST /api/quick-questions/${id} failed: ${(error as ApiError).status}`); }
}
